import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import SafeIcon from '../common/SafeIcon.jsx';
import { FiChevronRight, FiAward, FiTool, FiZap, FiBook } from 'react-icons/fi';
import { thinkingModes } from '../data/modes.js';
import { mathTypes } from '../data/mathTypes.js';
import { toolTypes } from '../data/toolTypes.js';
import ScrollToTop from '../utils/ScrollToTop';

export default function Results({ scores, onNext }) {
  const dominantMode = Object.keys(scores.mode).reduce((a, b) => scores.mode[a] > scores.mode[b] ? a : b);
  const dominantMath = Object.keys(scores.math).reduce((a, b) => scores.math[a] > scores.math[b] ? a : b);
  const dominantTool = Object.keys(scores.tool).reduce((a, b) => scores.tool[a] > scores.tool[b] ? a : b);

  const modeProfile = thinkingModes[dominantMode] || thinkingModes.structure;
  const mathProfile = mathTypes[dominantMath] || mathTypes.calculating;
  const toolProfile = toolTypes[dominantTool] || toolTypes.systematic;

  const radarData = useMemo(() => [
    { subject: 'Metakognition', A: modeProfile.maps.M, fullMark: 5 },
    { subject: 'Abstraktion', A: modeProfile.maps.A, fullMark: 5 },
    { subject: 'Persistenz', A: modeProfile.maps.P, fullMark: 5 },
    { subject: 'Systematik', A: modeProfile.maps.S, fullMark: 5 },
    { subject: 'Tool-Affinität', A: scores.tool[dominantTool] * 2 || 2, fullMark: 5 },
    { subject: 'Lerntyp', A: scores.math[dominantMath] * 2 || 2, fullMark: 5 },
  ], [modeProfile, scores, dominantTool, dominantMath]);

  const textColors = {
    blue: "text-blue-400",
    orange: "text-orange-400",
    fuchsia: "text-fuchsia-400",
    emerald: "text-emerald-400",
    cyan: "text-cyan-400",
  };

  const pillColors = {
    blue: "text-blue-300 bg-blue-500/10 border-blue-500/20",
    orange: "text-orange-300 bg-orange-500/10 border-orange-500/20",
    fuchsia: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    cyan: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
  };

  const toolColors = {
    violet: "text-violet-300 bg-violet-500/10 border-violet-500/20",
    cyan: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  };

  const activeTextColor = textColors[modeProfile.color] || "text-blue-400";
  const activePillColor = pillColors[modeProfile.color] || pillColors.blue;
  const activeToolColor = toolColors[toolProfile.color] || toolColors.emerald;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto w-full pt-4 pb-20"
    >
      <ScrollToTop />

      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-sm mb-4">
          <SafeIcon icon={FiAward} />
          <span>Vollständige Analyse</span>
        </div>
        <h2 className="text-5xl font-black text-white mb-4">Dein kognitives Profil</h2>
        <p className="text-slate-400 max-w-xl mx-auto">3 Dimensionen · Denkmodus × Lerntyp × Tool-Präferenz</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 mb-10">
        <div className="lg:col-span-8 space-y-6">
          <div className="p-10 bg-slate-800/50 border border-slate-700/60 rounded-[48px] relative overflow-hidden backdrop-blur-md shadow-2xl">
            <div className="relative z-10">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border mb-6 text-xs font-black uppercase tracking-[0.2em] ${activePillColor}`}>
                <SafeIcon icon={FiZap} />
                <span>Primärer Denkmodus</span>
              </div>
              <h3 className="text-6xl font-black text-white mb-6 leading-tight">
                Der <span className={activeTextColor}>{modeProfile.title}</span>
              </h3>
              <p className="text-xl text-slate-200 leading-relaxed mb-8 max-w-2xl">
                {modeProfile.description}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-6 bg-slate-900/60 rounded-3xl border border-slate-700/50 shadow-inner">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiBook} className="text-slate-400 text-xs" />
                    <div className="text-[10px] uppercase text-slate-400 font-black tracking-[0.2em]">Mathe-Lerntyp</div>
                  </div>
                  <div className="text-white font-bold text-lg mb-1">{mathProfile.title}</div>
                  <div className="text-blue-300 text-xs font-medium">{mathProfile.label}</div>
                </div>
                <div className="p-6 bg-slate-900/60 rounded-3xl border border-slate-700/50 shadow-inner">
                  <div className="text-[10px] uppercase text-slate-400 font-black mb-2 tracking-[0.2em]">DLF-Protokoll</div>
                  <div className="text-white font-bold text-lg mb-1">Erfolgs-Strategie</div>
                  <div className="text-emerald-400 text-xs uppercase font-black tracking-tighter">{modeProfile.dlfStrategy}</div>
                </div>
              </div>
            </div>
          </div>

          <div className={`p-8 rounded-[32px] border ${activeToolColor} backdrop-blur-md`}>
            <div className="flex items-center gap-3 mb-4">
              <SafeIcon icon={FiTool} className="text-lg" />
              <span className="text-sm font-black uppercase tracking-[0.2em]">Tool-Präferenz</span>
            </div>
            <h4 className="text-2xl font-black text-white mb-2">{toolProfile.title}</h4>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">{toolProfile.description}</p>
            <div className="flex flex-wrap gap-2">
              {toolProfile.tools.map((t, i) => (
                <span key={i} className="px-3 py-1 bg-slate-900/60 border border-slate-700 rounded-full text-xs text-slate-300 font-bold">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 self-stretch">
          <div className="h-full bg-slate-900 border-4 border-slate-800 rounded-[48px] p-8 flex flex-col items-center justify-center shadow-2xl">
            <h3 className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mb-6">MAPS+ Metriken</h3>
            <div className="w-full h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                  <PolarGrid stroke="#475569" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#cbd5e1', fontSize: 9, fontWeight: 'bold' }} />
                  <Radar name="MAPS+" dataKey="A" stroke="#60a5fa" strokeWidth={3} fill="#3b82f6" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-800 w-full">
              <div className="flex justify-between items-center text-[10px] text-slate-400 font-black uppercase tracking-widest">
                <span>6 Dimensionen</span>
                <span className="text-emerald-400">Kalibriert</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={onNext}
          className="group px-12 py-7 bg-white text-slate-900 font-black rounded-[32px] text-2xl flex items-center gap-4 hover:bg-white/90 transition-all shadow-2xl active:scale-95"
        >
          Mission & Strategie festlegen
          <SafeIcon icon={FiChevronRight} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </motion.div>
  );
}